/**
 * Last-known answers for the access gates the shell waits on.
 *
 * The sidebar menu and the home shortcuts cannot render until these resolve,
 * and resolving them costs a round trip per agency. Holding the previous answer
 * lets both paint immediately on the next navigation while the real value is
 * recomputed behind it — the recompute then re-renders through the usual
 * permission stream.
 *
 * Only booleans and small plain objects belong here: never anything that
 * decides a server-side outcome. The server remains the only enforcement point,
 * so the worst a stale entry can do is show a nav item that then denies.
 */
const GATE_CACHE_PREFIX = 'eh.gates.v1.';
const GATE_CACHE_TTL_MS = 12 * 60 * 60 * 1000;

interface CachedGate<T> {
  savedAt: number;
  value: T;
}

function gateKey(scope: string, name: string): string {
  return `${GATE_CACHE_PREFIX}${scope}.${name}`;
}

export function readGate<T>(scope: string, name: string): T | undefined {
  if (!scope) {
    return undefined;
  }
  try {
    const raw = localStorage.getItem(gateKey(scope, name));
    if (!raw) {
      return undefined;
    }
    const cached = JSON.parse(raw) as CachedGate<T>;
    // A missing or non-numeric `savedAt` makes the age NaN, and a future one
    // makes it negative — both slip past a plain `> TTL` check and never
    // expire. Drop anything that does not look like a record we wrote.
    const age = Date.now() - cached?.savedAt;
    if (
      cached?.value === undefined ||
      !Number.isFinite(age) ||
      age < 0 ||
      age > GATE_CACHE_TTL_MS
    ) {
      localStorage.removeItem(gateKey(scope, name));
      return undefined;
    }
    return cached.value;
  } catch {
    return undefined;
  }
}

export function writeGate<T>(scope: string, name: string, value: T): void {
  if (!scope) {
    return;
  }
  try {
    localStorage.setItem(
      gateKey(scope, name),
      JSON.stringify({ savedAt: Date.now(), value } satisfies CachedGate<T>)
    );
  } catch {
    // Private mode or a full quota: the gate still resolves normally.
  }
}

/** Sign-out and account switches must not leave another user's answers behind. */
export function clearGates(): void {
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(GATE_CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
